import { SearchBoxCore, SessionToken } from '@mapbox/search-js-core';

const { accessToken = null } = window.mapbox_config;

const GEOLOCATION_ERROR_CODE_MESSAGES = {
    1: 'Der Zugriff auf den Standort wurde abgelehnt. Um diese Funktion zu nutzen, müssen Sie über Ihren Browser den Zugriff auf Ihren Standort gewähren.',
    2: 'Ihre Position konnte nicht ermittelt werden. Eventuell ist der Zugriff auf den Standort über das Betriebssystem deaktiviert, oder Ihr Gerät kann den Standort nicht ermittlen.',
    3: 'Ihre Position kann aktuell nicht bestimmt werden. Bitte versuchen Sie es später erneut.',
};

const GEOLOCATION_UNKNOWN_ERROR_MESSAGE = 'Ein unbekannter Fehler ist aufgetreten.';

const sessionToken = new SessionToken();

const searchBox = new SearchBoxCore({
    accessToken,
});

const suggestionsByListItem = new WeakMap();

const suggestionLabel = suggestion => {
    if (suggestion.feature_type === 'address') {
        return suggestion.full_address;
    }

    if (suggestion.feature_type === 'street') {
        return `${suggestion.name}, ${suggestion.place_formatted}`;
    }

    if (suggestion.feature_type === 'postcode') {
        return `${suggestion.name} ${suggestion.context.place.name}, ${suggestion.context.country.name}`;
    }

    if (suggestion.feature_type === 'place') {
        return `${suggestion.name}, ${suggestion.context.country.name}`;
    }

    return suggestion.name;
};

const createSuggestionsList = response => {
    const list = document.createElement('ol');
    list.className = 'search-box__suggestions';

    response.suggestions.forEach(suggestion => {
        const button = document.createElement('button');
        button.className = 'search-box__suggestion-button button button--small';
        button.textContent = suggestionLabel(suggestion);

        const item = document.createElement('li');
        item.className = 'search-box__suggestion-item';
        item.appendChild(button);
        list.appendChild(item);

        suggestionsByListItem.set(button, suggestion);
    });

    return list;
};

const createAlert = (message, type = 'error') => {
    const alert = document.createElement('p');
    alert.className = `search-box__alert alert alert--${type}`;
    alert.textContent = message;
    return alert;
};

document.querySelectorAll('.mapbox-search-box-container').forEach(container => {
    const searchInput = container.querySelector('.search-box__input');
    const browserGeolocationButton = container.querySelector('.search-box__button');
    const latitudeInput = container.closest('form').querySelector('input[name="latitude"]');
    const longitudeInput = container.closest('form').querySelector('input[name="longitude"]');
    const liveArea = container.querySelector('.search-box__live-area');

    const updateCoordinateInputs = (latitude, longitude) => {
        latitudeInput.value = latitude;
        longitudeInput.value = longitude;
    };

    const updateCoordinateInputsAndSubmit = (latitude, longitude) => {
        updateCoordinateInputs(latitude, longitude);
        container.closest('form')?.requestSubmit();
    };

    const updateBySearchInput = async () => {
        const consentState = window.CookieConsent.getUserPreferences();
        const hasConsent = consentState.acceptedServices['media']?.includes('mapbox');
        if (!hasConsent) {
            const alert = createAlert(
                'Wir nutzen Mapbox für die Standort-Suche. Bitte aktivieren Sie Mapbox in den Datenschutzeinstellungen, um diese Funktion zu nutzen.',
                'error',
            );
            return liveArea.replaceChildren(alert);
        }

        if (searchInput.value.length === 0) {
            updateCoordinateInputs('', '');
        }

        if (searchInput.value.length < 3) {
            return liveArea.replaceChildren();
        }

        const response = await searchBox.suggest(searchInput.value, {
            sessionToken,
            limit: 5,
            language: document.documentElement.lang?.substring(0, 2) || 'de',
            country: 'DE,AT,CH',
            types: ['address', 'street', 'postcode', 'place'],
        });

        if (response.suggestions.length === 0) {
            const alert = createAlert('Keine Ergebnisse gefunden.', 'warning');
            return liveArea.replaceChildren(alert);
        }

        const suggestionsList = createSuggestionsList(response);
        liveArea.replaceChildren(suggestionsList);
    };

    const updateByBrowserGeolocation = async () => {
        let position;
        try {
            position = await new Promise((resolve, reject) => {
                navigator.geolocation.getCurrentPosition(resolve, reject, {
                    timeout: 5_000,
                    maximumAge: 30_000,
                });
            });
        } catch (error) {
            const message = GEOLOCATION_ERROR_CODE_MESSAGES[error.code];
            alert(message ?? GEOLOCATION_UNKNOWN_ERROR_MESSAGE);
        }

        const { latitude, longitude } = position.coords;
        searchInput.value = '';
        updateCoordinateInputsAndSubmit(latitude, longitude);
    };

    const handleSuggestionButtonClick = async e => {
        const button = e.target.closest('.search-box__suggestion-button');
        if (!button) return;
        e.preventDefault();

        const suggestions = suggestionsByListItem.get(button);
        const result = await searchBox.retrieve(suggestions, { sessionToken });

        const coordinates = result.features?.[0]?.geometry?.coordinates;
        if (!coordinates) {
            console.error('The selected suggestion has no coordinates.', result);
            return;
        }

        searchInput.value = button.textContent;
        liveArea.replaceChildren();
        updateCoordinateInputsAndSubmit(coordinates[1], coordinates[0]);
    };

    const handleKeyboardNavigation = e => {
        if (e.code === 'Enter' && document.activeElement === searchInput) {
            e.preventDefault();
            const firstSuggestionButton = liveArea.querySelector('.search-box__suggestion-button');
            firstSuggestionButton?.click();
        }

        if (['ArrowUp', 'ArrowDown'].includes(e.code)) {
            const suggestions = container.getElementsByClassName('search-box__suggestion-button');
            if (suggestions.length === 0) return;

            e.preventDefault();
            const currentIndex = Array.from(suggestions).findIndex(
                el => el === document.activeElement,
            );
            let nextIndex;
            switch (e.code) {
                case 'ArrowUp':
                    nextIndex = currentIndex > 0 ? currentIndex - 1 : suggestions.length - 1;
                    break;
                case 'ArrowDown':
                    nextIndex = currentIndex < suggestions.length - 1 ? currentIndex + 1 : 0;
                    break;
            }
            suggestions[nextIndex]?.focus();
        }
    };

    browserGeolocationButton.addEventListener('click', updateByBrowserGeolocation);
    searchInput.addEventListener('input', updateBySearchInput);
    liveArea.addEventListener('click', handleSuggestionButtonClick);
    container.addEventListener('keydown', handleKeyboardNavigation);
});
